import React, { useEffect, useState } from "react";
import Layout from "../../Components/Layout";
import {
  Download,
  Eye,
  FileSpreadsheet,
  LoaderCircle,
  Send,
  X,
} from "lucide-react";
import * as XLSX from "xlsx";
import axios from "axios";
import toast from "react-hot-toast";
import "./SalaryAnnexure.css";
import { useNavigate } from "react-router-dom";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// API function to fetch salary annexure
const fetchSalaryAnnexure = async ({ month, year }) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/v1/salary/slip`,
      {
        month: month,
        year: year,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching salary annexure:", error);
    throw error;
  }
};

const sendSalaryAnnexure = async ({ month, year }) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/api/v1/salary/slip/publish`,
      {},
      {
        params: {
          year,
          month,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error publishing salary annexure:", error);
    throw error;
  }
};

const formatCurrency = (value) => `₹${value.toLocaleString("en-IN")}`;

function SalaryAnnexure() {
  const currentDate = new Date();
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(true);
  const [month, setMonth] = useState(String(currentDate.getMonth() + 1));
  const [year, setYear] = useState(String(currentDate.getFullYear()));
  const [period, setPeriod] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    document.title = "Salary Annexure";
    return () => {
      document.title = "Employee Management";
    };
  }, []);

  const handlePeriodSubmit = async (event) => {
    event.preventDefault();

    // Check if token exists
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token not found. Please login again.");
      return;
    }

    setIsLoading(true);
    const selectedPeriod = { month: Number(month), year: Number(year) };

    try {
      const response = await fetchSalaryAnnexure(selectedPeriod);

      // Assuming the API returns data in a similar structure
      // Adjust this based on your actual API response structure
      setPeriod({ month: selectedPeriod.month, year: selectedPeriod.year });

      // If your API returns employees data directly
      if (response.employees && Array.isArray(response.employees)) {
        setEmployees(response.employees);
        toast.success(`Loaded ${response.employees.length} employees`);
      } else if (Array.isArray(response)) {
        // If API returns array directly
        setEmployees(response);
        toast.success(`Loaded ${response.length} employees`);
      } else {
        // Handle other response structures - adjust based on your API
        console.warn("Unexpected API response structure:", response);
        toast.warning("Unexpected data format received from server");
        setEmployees([]);
      }

      setIsPeriodModalOpen(false);
    } catch (error) {
      console.error("Failed to load salary annexure:", error);

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const statusCode = error.response.status;
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          "Failed to load salary annexure";

        if (statusCode === 401) {
          toast.error("Authentication failed. Please login again.");
        } else if (statusCode === 403) {
          toast.error("You don't have permission to access this data.");
        } else if (statusCode === 404) {
          toast.error("Salary data not found for the selected period.");
        } else {
          toast.error(errorMessage);
        }
      } else if (error.request) {
        // The request was made but no response was received
        toast.error("No response from server. Please check your connection.");
      } else {
        // Something happened in setting up the request
        // toast.error(error.message || "An unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    const headers = [
      "Sr. No.",
      "EMP ID",
      "Name of Employees",
      "Designation",
      "Name of Site",
      "Date of Joining",
      "No. of Days in a Month",
      "No. of Present Days Present",
      "Basic Pay",
      "HRA",
      "Conv Allow/VEHICLE REIMB",
      "Child Education Allow",
      "LTA Monthly",
      "Medical Monthly",
      "Attire Reimb",
      "Sp.All",
      "Other Allow",
      "Gross Amount",
      "Prof. Tax",
      "ESIC",
      "P. F. Cont.",
      "TDS",
      "Advance/Other Deductions",
      "Total Ded",
      "Net Amt",
      "OTHER Reimbursements",
      "Total Payable",
    ];

    const getItemAmount = (employee, componentName) =>
      employee.items?.find((item) => item.componentName === componentName)
        ?.actualAmount || 0;

    const rows = employees.map((employee, index) => ({
      "Sr. No.": index + 1,
      "EMP ID": employee.empId || "-",
      "Name of Employees": employee.employeeName || "-",
      Designation: employee.designation || "-",
      "Name of Site": employee.nameOfSite || employee.siteName || "-",
      "Date of Joining": employee.dateOfJoining || "-",
      "No. of Days in a Month": employee.totalDaysInMonth || 0,
      "No. of Present Days Present": employee.payableDays || 0,
      "Basic Pay": getItemAmount(employee, "BASIC"),
      HRA: getItemAmount(employee, "HRA"),
      "Conv Allow/VEHICLE REIMB": getItemAmount(employee, "CONVEYANCE"),
      "Child Education Allow": getItemAmount(employee, "CHILD_EDUCATION"),
      "LTA Monthly": getItemAmount(employee, "LTA"),
      "Medical Monthly": getItemAmount(employee, "MEDICAL"),
      "Attire Reimb": getItemAmount(employee, "ATTIRE"),
      "Sp.All": getItemAmount(employee, "SPECIAL_ALLOWANCE"),
      "Other Allow": getItemAmount(employee, "OTHER_ALLOWANCE"),
      "Gross Amount": employee.totalEarning || 0,
      "Prof. Tax": getItemAmount(employee, "PROFESSIONAL_TAX"),
      ESIC: getItemAmount(employee, "ESIC"),
      "P. F. Cont.": getItemAmount(employee, "PF_EMPLOYEE"),
      TDS: getItemAmount(employee, "TDS"),
      "Advance/Other Deductions": getItemAmount(employee, "OTHER_DEDUCTION"),
      "Total Ded": employee.totalDeduction || 0,
      "Net Amt": employee.netSalary || 0,
      "OTHER Reimbursements": getItemAmount(employee, "OTHER_REIMBURSEMENTS"),
      "Total Payable": employee.totalPayable || employee.netSalary || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    const currencyColumns = new Set([
      8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26,
    ]);
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: column })];
      headerCell.s = {
        fill: {
          patternType: "solid",
          fgColor: { rgb: "1F4E78" },
        },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "D9E2F3" } },
          bottom: { style: "thin", color: { rgb: "D9E2F3" } },
          left: { style: "thin", color: { rgb: "D9E2F3" } },
          right: { style: "thin", color: { rgb: "D9E2F3" } },
        },
      };
    }

    for (let row = 1; row <= range.e.r; row += 1) {
      for (let column = range.s.c; column <= range.e.c; column += 1) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
        if (!cell) continue;
        cell.s = {
          alignment: { vertical: "center", wrapText: true },
          border: {
            bottom: { style: "thin", color: { rgb: "D9E2F3" } },
          },
        };
        if (currencyColumns.has(column)) cell.z = "#,##0.00";
      }
    }

    worksheet["!freeze"] = { xSplit: 2, ySplit: 1 };
    worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
    worksheet["!cols"] = headers.map((header, index) => ({
      wch:
        index === 2 ? 24 : index === 5 ? 15 : Math.max(header.length + 2, 14),
    }));
    worksheet["!rows"] = [{ hpt: 32 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Salary Annexure");
    XLSX.writeFile(
      workbook,
      `salary-annexure-${MONTHS[period.month - 1]}-${period.year}.xlsx`,
    );
    toast.success("Excel file downloaded successfully");
  };

  const handleSend = async () => {
    if (!period) return;

    setIsSending(true);
    setSendStatus("");

    try {
      const response = await sendSalaryAnnexure({
        month: period.month,
        year: period.year,
      });

      const successMessage =
        response?.message ||
        `${employees.length} employees queued for notification.`;

      setSendStatus(successMessage);
      toast.success(successMessage);
    } catch (error) {
      console.error("Failed to send notifications:", error);

      let errorMessage = "Failed to send notifications. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      toast.error(errorMessage);
      setSendStatus(errorMessage);
    } finally {
      setIsSending(false);
    }
  };
  // Add useNavigate import at top

  // Inside the component
  const navigate = useNavigate();

  // Replace the view button onClick handler
  const handleViewEmployee = (empId) => {
    // Navigate to PDF page with empId, month, year
    navigate("/salaryslip-pdf", {
      state: {
        empId: empId,
        month: period.month,
        year: period.year,
      },
    });
  };
  return (
    <Layout>
      <div className="backgound-main-content salary-annexure-page">
        <div className="salary-annexure-header">
          <div>
            <p className="salary-annexure-eyebrow">PAYROLL OPERATIONS</p>
            <h1>Salary Annexure</h1>
            <p className="salary-annexure-subtitle">
              Review attendance and daily salary details for your employees.
            </p>
          </div>
          {period && (
            <button
              className="salary-period-button"
              type="button"
              onClick={() => setIsPeriodModalOpen(true)}
            >
              <span className="salary-period-label">PAY PERIOD</span>
              {MONTHS[period.month - 1]} {period.year}
            </button>
          )}
        </div>

        <section
          className="salary-annexure-card"
          aria-labelledby="salary-table-title"
        >
          <div className="salary-table-heading">
            <div>
              <h2 id="salary-table-title">Employee salary details</h2>
              <p>{employees.length} employees included in this annexure</p>
            </div>
            <div className="salary-table-actions">
              <button
                className="salary-secondary-button"
                type="button"
                onClick={handleDownload}
                disabled={!employees.length}
              >
                <Download size={16} aria-hidden="true" /> Download Excel
              </button>
              <button
                className="salary-primary-button"
                type="button"
                onClick={handleSend}
                disabled={!employees.length || isSending}
              >
                <Send size={16} aria-hidden="true" />{" "}
                {isSending ? "Sending..." : "Send to Employees"}
              </button>
            </div>
          </div>
          {sendStatus && (
            <p className="salary-send-status" role="status">
              {sendStatus}
            </p>
          )}

          <div className="salary-table-wrap">
            <table className="salary-annexure-table">
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Employee Name</th>
                  <th>Attendance</th>
                  <th>Net Salary</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee, index) => {
                  // IMPORTANT: Use empId from API response first
                  const empId = employee.empId;

                  const empName = employee.employeeName;
                  const empAttendance = employee.payableDays || 0;
                  const empSalary = employee.netSalary || 0;

                  return (
                    <tr key={empId || index}>
                      <td>
                        <span className="employee-id">{empId}</span>
                      </td>
                      <td className="employee-name">{empName}</td>
                      <td>{empAttendance} days</td>
                      <td className="salary-value">
                        {formatCurrency(empSalary)}
                      </td>
                      <td>
                        <button
                          className="salary-view-button"
                          type="button"
                          onClick={() => handleViewEmployee(empId)} // Pass only empId
                          aria-label={`View ${empName}'s salary details`}
                          title="View salary details"
                        >
                          <Eye size={17} aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {isPeriodModalOpen && (
          <div className="salary-modal-backdrop" role="presentation">
            <div
              className="salary-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="period-modal-title"
            >
              <div className="salary-modal-icon">
                <FileSpreadsheet size={22} aria-hidden="true" />
              </div>
              <h2 id="period-modal-title">Select pay period</h2>
              <p>Choose a month and year to load the salary annexure.</p>
              <form onSubmit={handlePeriodSubmit}>
                <div className="salary-form-grid">
                  <label>
                    Month
                    <select
                      value={month}
                      onChange={(event) => setMonth(event.target.value)}
                    >
                      {MONTHS.map((monthName, index) => (
                        <option value={index + 1} key={monthName}>
                          {monthName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Year
                    <select
                      value={year}
                      onChange={(event) => setYear(event.target.value)}
                    >
                      {[
                        currentDate.getFullYear() - 1,
                        currentDate.getFullYear(),
                      ].map((yearOption) => (
                        <option value={yearOption} key={yearOption}>
                          {yearOption}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="salary-modal-actions">
                  <button
                    className="salary-secondary-button"
                    type="button"
                    onClick={() => {
                      setIsPeriodModalOpen(false);
                      navigate(-1);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="salary-primary-button salary-load-button"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <LoaderCircle
                          size={16}
                          className="salary-spinner"
                          aria-hidden="true"
                        />{" "}
                        Loading annexure...
                      </>
                    ) : (
                      "Load salary annexure"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {selectedEmployee && (
          <div
            className="salary-modal-backdrop"
            role="presentation"
            onClick={() => setSelectedEmployee(null)}
          >
            <div
              className="salary-detail-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="employee-detail-title"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                className="salary-close-button"
                type="button"
                onClick={() => setSelectedEmployee(null)}
                aria-label="Close details"
              >
                <X size={18} />
              </button>
              <p className="salary-annexure-eyebrow">EMPLOYEE DETAIL</p>
              <h2 id="employee-detail-title">
                {selectedEmployee.employeeName}
              </h2>
              <p className="salary-detail-id">{selectedEmployee.employeeId}</p>
              <div className="salary-detail-grid">
                <span>
                  Attendance<strong>{selectedEmployee.attendance} days</strong>
                </span>
                <span>
                  Per day salary
                  <strong>
                    {formatCurrency(selectedEmployee.perDaySalary)}
                  </strong>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default SalaryAnnexure;
